/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ArchiveSystemParams,
  ArchiveSystemPathParameters,
  GetSystemConnectorFamilyIconPathParameters,
  GetSystemConnectorIconPathParameters,
  GetSystemConnectorPathParameters,
  GetSystemPathParameters,
  ListSystemAuditParams,
  ListSystemAuditPathParameters,
  ListSystemConnectorsParams,
  ListSystemDependentsPathParameters,
  ListSystemsParams,
  PurgeSystemPathParameters,
  TestSystemPathParameters,
  TLHTTPValidationError,
  TLIntegrationsAuditList,
  TLIntegrationsConnectorDetail,
  TLIntegrationsConnectorList,
  TLIntegrationsDependentList,
  TLIntegrationsSystem,
  TLIntegrationsSystemCreateRequest,
  TLIntegrationsSystemList,
  TLIntegrationsSystemPurgeRequest,
  TLIntegrationsSystemTestRequest,
  TLIntegrationsSystemUpdateRequest,
  TLIntegrationsSystemValidateRequest,
  TLIntegrationsTestResult,
  TLIntegrationsValidationResult,
  UpdateSystemPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Systems
 */
export const listSystems = (
  params?: ListSystemsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsSystemList>({ url: `/systems`, method: 'GET', params, signal }, options);
};

export const getListSystemsQueryKey = (params?: ListSystemsParams) => {
  return ['systems', ...(params ? [params] : [])] as const;
};

export const getListSystemsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListSystemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSystemsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSystems>>> = ({ signal }) =>
    listSystems(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSystems>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSystemsQueryResult = NonNullable<Awaited<ReturnType<typeof listSystems>>>;
export type ListSystemsQueryError = ErrorType<TLHTTPValidationError>;

export function useListSystems<
  TData = Awaited<ReturnType<typeof listSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListSystemsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystems>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSystems>>,
          TError,
          Awaited<ReturnType<typeof listSystems>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSystems<
  TData = Awaited<ReturnType<typeof listSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListSystemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystems>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSystems>>,
          TError,
          Awaited<ReturnType<typeof listSystems>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSystems<
  TData = Awaited<ReturnType<typeof listSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListSystemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Systems
 */

export function useListSystems<
  TData = Awaited<ReturnType<typeof listSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListSystemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSystemsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Systems
 */
export const prefetchListSystemsQuery = async <
  TData = Awaited<ReturnType<typeof listSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListSystemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListSystemsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Systems
 */
export const invalidateListSystems = async (
  queryClient: QueryClient,
  params?: ListSystemsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListSystemsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create System
 */
export const createSystem = (
  tLIntegrationsSystemCreateRequest: BodyType<TLIntegrationsSystemCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsSystem>(
    {
      url: `/systems`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLIntegrationsSystemCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateSystemQueryKey = (
  tLIntegrationsSystemCreateRequest?: BodyType<TLIntegrationsSystemCreateRequest>,
) => {
  return ['POST', 'systems', tLIntegrationsSystemCreateRequest] as const;
};

export const getCreateSystemQueryOptions = <
  TData = Awaited<ReturnType<typeof createSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemCreateRequest: BodyType<TLIntegrationsSystemCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateSystemQueryKey(tLIntegrationsSystemCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createSystem>>> = ({ signal }) =>
    createSystem(tLIntegrationsSystemCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createSystem>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateSystemQueryResult = NonNullable<Awaited<ReturnType<typeof createSystem>>>;
export type CreateSystemQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateSystem<
  TData = Awaited<ReturnType<typeof createSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemCreateRequest: BodyType<TLIntegrationsSystemCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSystem>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createSystem>>,
          TError,
          Awaited<ReturnType<typeof createSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateSystem<
  TData = Awaited<ReturnType<typeof createSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemCreateRequest: BodyType<TLIntegrationsSystemCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSystem>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createSystem>>,
          TError,
          Awaited<ReturnType<typeof createSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateSystem<
  TData = Awaited<ReturnType<typeof createSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemCreateRequest: BodyType<TLIntegrationsSystemCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create System
 */

export function useCreateSystem<
  TData = Awaited<ReturnType<typeof createSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemCreateRequest: BodyType<TLIntegrationsSystemCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateSystemQueryOptions(tLIntegrationsSystemCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create System
 */
export const prefetchCreateSystemQuery = async <
  TData = Awaited<ReturnType<typeof createSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLIntegrationsSystemCreateRequest: BodyType<TLIntegrationsSystemCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateSystemQueryOptions(tLIntegrationsSystemCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create System
 */
export const invalidateCreateSystem = async (
  queryClient: QueryClient,
  tLIntegrationsSystemCreateRequest: BodyType<TLIntegrationsSystemCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateSystemQueryKey(tLIntegrationsSystemCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary List System Connectors
 */
export const listSystemConnectors = (
  params?: ListSystemConnectorsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsConnectorList>(
    { url: `/systems/connectors`, method: 'GET', params, signal },
    options,
  );
};

export const getListSystemConnectorsQueryKey = (params?: ListSystemConnectorsParams) => {
  return ['systems', 'connectors', ...(params ? [params] : [])] as const;
};

export const getListSystemConnectorsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSystemConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListSystemConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemConnectors>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSystemConnectorsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSystemConnectors>>> = ({ signal }) =>
    listSystemConnectors(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSystemConnectors>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSystemConnectorsQueryResult = NonNullable<Awaited<ReturnType<typeof listSystemConnectors>>>;
export type ListSystemConnectorsQueryError = ErrorType<TLHTTPValidationError>;

export function useListSystemConnectors<
  TData = Awaited<ReturnType<typeof listSystemConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListSystemConnectorsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemConnectors>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSystemConnectors>>,
          TError,
          Awaited<ReturnType<typeof listSystemConnectors>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSystemConnectors<
  TData = Awaited<ReturnType<typeof listSystemConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListSystemConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemConnectors>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSystemConnectors>>,
          TError,
          Awaited<ReturnType<typeof listSystemConnectors>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSystemConnectors<
  TData = Awaited<ReturnType<typeof listSystemConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListSystemConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemConnectors>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List System Connectors
 */

export function useListSystemConnectors<
  TData = Awaited<ReturnType<typeof listSystemConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListSystemConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemConnectors>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSystemConnectorsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List System Connectors
 */
export const prefetchListSystemConnectorsQuery = async <
  TData = Awaited<ReturnType<typeof listSystemConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListSystemConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemConnectors>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListSystemConnectorsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List System Connectors
 */
export const invalidateListSystemConnectors = async (
  queryClient: QueryClient,
  params?: ListSystemConnectorsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListSystemConnectorsQueryKey(params) }, options);

  return queryClient;
};

/**
 * Serve a connector family's brand SVG. Browsers reach this through the frontend `/api`
 * proxy, which attaches the session token, so an <img> tag needs no credentials of its own.
 * @summary Get System Connector Family Icon
 */
export const getSystemConnectorFamilyIcon = (
  { familySlug }: GetSystemConnectorFamilyIconPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/systems/connectors/families/${familySlug}/icon`, method: 'GET', signal }, options);
};

export const getGetSystemConnectorFamilyIconQueryKey = ({ familySlug }: GetSystemConnectorFamilyIconPathParameters) => {
  return ['systems', 'connectors', 'families', familySlug, 'icon'] as const;
};

export const getGetSystemConnectorFamilyIconQueryOptions = <
  TData = Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { familySlug }: GetSystemConnectorFamilyIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSystemConnectorFamilyIconQueryKey({ familySlug });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>> = ({ signal }) =>
    getSystemConnectorFamilyIcon({ familySlug }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: familySlug !== null && familySlug !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetSystemConnectorFamilyIconQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>
>;
export type GetSystemConnectorFamilyIconQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSystemConnectorFamilyIcon<
  TData = Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorFamilyIconPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>,
          TError,
          Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSystemConnectorFamilyIcon<
  TData = Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorFamilyIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>,
          TError,
          Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSystemConnectorFamilyIcon<
  TData = Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorFamilyIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get System Connector Family Icon
 */

export function useGetSystemConnectorFamilyIcon<
  TData = Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { familySlug }: GetSystemConnectorFamilyIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSystemConnectorFamilyIconQueryOptions({ familySlug }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get System Connector Family Icon
 */
export const prefetchGetSystemConnectorFamilyIconQuery = async <
  TData = Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { familySlug }: GetSystemConnectorFamilyIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorFamilyIcon>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSystemConnectorFamilyIconQueryOptions({ familySlug }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get System Connector Family Icon
 */
export const invalidateGetSystemConnectorFamilyIcon = async (
  queryClient: QueryClient,
  { familySlug }: GetSystemConnectorFamilyIconPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSystemConnectorFamilyIconQueryKey({ familySlug }) }, options);

  return queryClient;
};

/**
 * @summary Get System Connector
 */
export const getSystemConnector = (
  { slug }: GetSystemConnectorPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsConnectorDetail>(
    { url: `/systems/connectors/${slug}`, method: 'GET', signal },
    options,
  );
};

export const getGetSystemConnectorQueryKey = ({ slug }: GetSystemConnectorPathParameters) => {
  return ['systems', 'connectors', slug] as const;
};

export const getGetSystemConnectorQueryOptions = <
  TData = Awaited<ReturnType<typeof getSystemConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { slug }: GetSystemConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSystemConnectorQueryKey({ slug });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSystemConnector>>> = ({ signal }) =>
    getSystemConnector({ slug }, requestOptions, signal);

  return { queryKey, queryFn, enabled: slug !== null && slug !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSystemConnector>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSystemConnectorQueryResult = NonNullable<Awaited<ReturnType<typeof getSystemConnector>>>;
export type GetSystemConnectorQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSystemConnector<
  TData = Awaited<ReturnType<typeof getSystemConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnector>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSystemConnector>>,
          TError,
          Awaited<ReturnType<typeof getSystemConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSystemConnector<
  TData = Awaited<ReturnType<typeof getSystemConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnector>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSystemConnector>>,
          TError,
          Awaited<ReturnType<typeof getSystemConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSystemConnector<
  TData = Awaited<ReturnType<typeof getSystemConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get System Connector
 */

export function useGetSystemConnector<
  TData = Awaited<ReturnType<typeof getSystemConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { slug }: GetSystemConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSystemConnectorQueryOptions({ slug }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get System Connector
 */
export const prefetchGetSystemConnectorQuery = async <
  TData = Awaited<ReturnType<typeof getSystemConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { slug }: GetSystemConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSystemConnectorQueryOptions({ slug }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get System Connector
 */
export const invalidateGetSystemConnector = async (
  queryClient: QueryClient,
  { slug }: GetSystemConnectorPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSystemConnectorQueryKey({ slug }) }, options);

  return queryClient;
};

/**
 * @summary Get System Connector Icon
 */
export const getSystemConnectorIcon = (
  { slug }: GetSystemConnectorIconPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/systems/connectors/${slug}/icon`, method: 'GET', signal }, options);
};

export const getGetSystemConnectorIconQueryKey = ({ slug }: GetSystemConnectorIconPathParameters) => {
  return ['systems', 'connectors', slug, 'icon'] as const;
};

export const getGetSystemConnectorIconQueryOptions = <
  TData = Awaited<ReturnType<typeof getSystemConnectorIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { slug }: GetSystemConnectorIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorIcon>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSystemConnectorIconQueryKey({ slug });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSystemConnectorIcon>>> = ({ signal }) =>
    getSystemConnectorIcon({ slug }, requestOptions, signal);

  return { queryKey, queryFn, enabled: slug !== null && slug !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSystemConnectorIcon>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSystemConnectorIconQueryResult = NonNullable<Awaited<ReturnType<typeof getSystemConnectorIcon>>>;
export type GetSystemConnectorIconQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSystemConnectorIcon<
  TData = Awaited<ReturnType<typeof getSystemConnectorIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorIconPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorIcon>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSystemConnectorIcon>>,
          TError,
          Awaited<ReturnType<typeof getSystemConnectorIcon>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSystemConnectorIcon<
  TData = Awaited<ReturnType<typeof getSystemConnectorIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorIcon>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSystemConnectorIcon>>,
          TError,
          Awaited<ReturnType<typeof getSystemConnectorIcon>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSystemConnectorIcon<
  TData = Awaited<ReturnType<typeof getSystemConnectorIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSystemConnectorIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorIcon>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get System Connector Icon
 */

export function useGetSystemConnectorIcon<
  TData = Awaited<ReturnType<typeof getSystemConnectorIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { slug }: GetSystemConnectorIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorIcon>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSystemConnectorIconQueryOptions({ slug }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get System Connector Icon
 */
export const prefetchGetSystemConnectorIconQuery = async <
  TData = Awaited<ReturnType<typeof getSystemConnectorIcon>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { slug }: GetSystemConnectorIconPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystemConnectorIcon>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSystemConnectorIconQueryOptions({ slug }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get System Connector Icon
 */
export const invalidateGetSystemConnectorIcon = async (
  queryClient: QueryClient,
  { slug }: GetSystemConnectorIconPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSystemConnectorIconQueryKey({ slug }) }, options);

  return queryClient;
};

/**
 * @summary Test System Draft
 */
export const testSystemDraft = (
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsTestResult>(
    {
      url: `/systems/test`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLIntegrationsSystemValidateRequest,
      signal,
    },
    options,
  );
};

export const getTestSystemDraftQueryKey = (
  tLIntegrationsSystemValidateRequest?: BodyType<TLIntegrationsSystemValidateRequest>,
) => {
  return ['POST', 'systems', 'test', tLIntegrationsSystemValidateRequest] as const;
};

export const getTestSystemDraftQueryOptions = <
  TData = Awaited<ReturnType<typeof testSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystemDraft>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getTestSystemDraftQueryKey(tLIntegrationsSystemValidateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof testSystemDraft>>> = ({ signal }) =>
    testSystemDraft(tLIntegrationsSystemValidateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof testSystemDraft>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type TestSystemDraftQueryResult = NonNullable<Awaited<ReturnType<typeof testSystemDraft>>>;
export type TestSystemDraftQueryError = ErrorType<TLHTTPValidationError>;

export function useTestSystemDraft<
  TData = Awaited<ReturnType<typeof testSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystemDraft>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof testSystemDraft>>,
          TError,
          Awaited<ReturnType<typeof testSystemDraft>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTestSystemDraft<
  TData = Awaited<ReturnType<typeof testSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystemDraft>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof testSystemDraft>>,
          TError,
          Awaited<ReturnType<typeof testSystemDraft>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTestSystemDraft<
  TData = Awaited<ReturnType<typeof testSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystemDraft>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Test System Draft
 */

export function useTestSystemDraft<
  TData = Awaited<ReturnType<typeof testSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystemDraft>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getTestSystemDraftQueryOptions(tLIntegrationsSystemValidateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Test System Draft
 */
export const prefetchTestSystemDraftQuery = async <
  TData = Awaited<ReturnType<typeof testSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystemDraft>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getTestSystemDraftQueryOptions(tLIntegrationsSystemValidateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Test System Draft
 */
export const invalidateTestSystemDraft = async (
  queryClient: QueryClient,
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getTestSystemDraftQueryKey(tLIntegrationsSystemValidateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Validate System Draft
 */
export const validateSystemDraft = (
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsValidationResult>(
    {
      url: `/systems/validate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLIntegrationsSystemValidateRequest,
      signal,
    },
    options,
  );
};

export const getValidateSystemDraftQueryKey = (
  tLIntegrationsSystemValidateRequest?: BodyType<TLIntegrationsSystemValidateRequest>,
) => {
  return ['POST', 'systems', 'validate', tLIntegrationsSystemValidateRequest] as const;
};

export const getValidateSystemDraftQueryOptions = <
  TData = Awaited<ReturnType<typeof validateSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSystemDraft>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getValidateSystemDraftQueryKey(tLIntegrationsSystemValidateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof validateSystemDraft>>> = ({ signal }) =>
    validateSystemDraft(tLIntegrationsSystemValidateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof validateSystemDraft>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ValidateSystemDraftQueryResult = NonNullable<Awaited<ReturnType<typeof validateSystemDraft>>>;
export type ValidateSystemDraftQueryError = ErrorType<TLHTTPValidationError>;

export function useValidateSystemDraft<
  TData = Awaited<ReturnType<typeof validateSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSystemDraft>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateSystemDraft>>,
          TError,
          Awaited<ReturnType<typeof validateSystemDraft>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateSystemDraft<
  TData = Awaited<ReturnType<typeof validateSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSystemDraft>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateSystemDraft>>,
          TError,
          Awaited<ReturnType<typeof validateSystemDraft>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateSystemDraft<
  TData = Awaited<ReturnType<typeof validateSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSystemDraft>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Validate System Draft
 */

export function useValidateSystemDraft<
  TData = Awaited<ReturnType<typeof validateSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSystemDraft>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getValidateSystemDraftQueryOptions(tLIntegrationsSystemValidateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Validate System Draft
 */
export const prefetchValidateSystemDraftQuery = async <
  TData = Awaited<ReturnType<typeof validateSystemDraft>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSystemDraft>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getValidateSystemDraftQueryOptions(tLIntegrationsSystemValidateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Validate System Draft
 */
export const invalidateValidateSystemDraft = async (
  queryClient: QueryClient,
  tLIntegrationsSystemValidateRequest: BodyType<TLIntegrationsSystemValidateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getValidateSystemDraftQueryKey(tLIntegrationsSystemValidateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Archive System
 */
export const archiveSystem = (
  { sysRef }: ArchiveSystemPathParameters,
  params?: ArchiveSystemParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/systems/${sysRef}`, method: 'DELETE', params, signal }, options);
};

export const getArchiveSystemQueryKey = ({ sysRef }: ArchiveSystemPathParameters, params?: ArchiveSystemParams) => {
  return ['DELETE', 'systems', sysRef, ...(params ? [params] : [])] as const;
};

export const getArchiveSystemQueryOptions = <
  TData = Awaited<ReturnType<typeof archiveSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: ArchiveSystemPathParameters,
  params?: ArchiveSystemParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getArchiveSystemQueryKey({ sysRef }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof archiveSystem>>> = ({ signal }) =>
    archiveSystem({ sysRef }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof archiveSystem>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ArchiveSystemQueryResult = NonNullable<Awaited<ReturnType<typeof archiveSystem>>>;
export type ArchiveSystemQueryError = ErrorType<TLHTTPValidationError>;

export function useArchiveSystem<
  TData = Awaited<ReturnType<typeof archiveSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveSystemPathParameters,
  params: undefined | ArchiveSystemParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveSystem>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof archiveSystem>>,
          TError,
          Awaited<ReturnType<typeof archiveSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useArchiveSystem<
  TData = Awaited<ReturnType<typeof archiveSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveSystemPathParameters,
  params?: ArchiveSystemParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveSystem>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof archiveSystem>>,
          TError,
          Awaited<ReturnType<typeof archiveSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useArchiveSystem<
  TData = Awaited<ReturnType<typeof archiveSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveSystemPathParameters,
  params?: ArchiveSystemParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Archive System
 */

export function useArchiveSystem<
  TData = Awaited<ReturnType<typeof archiveSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: ArchiveSystemPathParameters,
  params?: ArchiveSystemParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getArchiveSystemQueryOptions({ sysRef }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Archive System
 */
export const prefetchArchiveSystemQuery = async <
  TData = Awaited<ReturnType<typeof archiveSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: ArchiveSystemPathParameters,
  params?: ArchiveSystemParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getArchiveSystemQueryOptions({ sysRef }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Archive System
 */
export const invalidateArchiveSystem = async (
  queryClient: QueryClient,
  { sysRef }: ArchiveSystemPathParameters,
  params?: ArchiveSystemParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getArchiveSystemQueryKey({ sysRef }, params) }, options);

  return queryClient;
};

/**
 * @summary Get System
 */
export const getSystem = (
  { sysRef }: GetSystemPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsSystem>({ url: `/systems/${sysRef}`, method: 'GET', signal }, options);
};

export const getGetSystemQueryKey = ({ sysRef }: GetSystemPathParameters) => {
  return ['systems', sysRef] as const;
};

export const getGetSystemQueryOptions = <
  TData = Awaited<ReturnType<typeof getSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: GetSystemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSystemQueryKey({ sysRef });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSystem>>> = ({ signal }) =>
    getSystem({ sysRef }, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSystem>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSystemQueryResult = NonNullable<Awaited<ReturnType<typeof getSystem>>>;
export type GetSystemQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSystem<TData = Awaited<ReturnType<typeof getSystem>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetSystemPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystem>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getSystem>>, TError, Awaited<ReturnType<typeof getSystem>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSystem<TData = Awaited<ReturnType<typeof getSystem>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetSystemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystem>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSystem>>,
          TError,
          Awaited<ReturnType<typeof getSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSystem<TData = Awaited<ReturnType<typeof getSystem>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetSystemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get System
 */

export function useGetSystem<TData = Awaited<ReturnType<typeof getSystem>>, TError = ErrorType<TLHTTPValidationError>>(
  { sysRef }: GetSystemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSystemQueryOptions({ sysRef }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get System
 */
export const prefetchGetSystemQuery = async <
  TData = Awaited<ReturnType<typeof getSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: GetSystemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSystemQueryOptions({ sysRef }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get System
 */
export const invalidateGetSystem = async (
  queryClient: QueryClient,
  { sysRef }: GetSystemPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSystemQueryKey({ sysRef }) }, options);

  return queryClient;
};

/**
 * @summary Update System
 */
export const updateSystem = (
  { sysRef }: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest: BodyType<TLIntegrationsSystemUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsSystem>(
    {
      url: `/systems/${sysRef}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLIntegrationsSystemUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateSystemQueryKey = (
  { sysRef }: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest?: BodyType<TLIntegrationsSystemUpdateRequest>,
) => {
  return ['PATCH', 'systems', sysRef, tLIntegrationsSystemUpdateRequest] as const;
};

export const getUpdateSystemQueryOptions = <
  TData = Awaited<ReturnType<typeof updateSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest: BodyType<TLIntegrationsSystemUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateSystemQueryKey({ sysRef }, tLIntegrationsSystemUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateSystem>>> = ({ signal }) =>
    updateSystem({ sysRef }, tLIntegrationsSystemUpdateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateSystem>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateSystemQueryResult = NonNullable<Awaited<ReturnType<typeof updateSystem>>>;
export type UpdateSystemQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateSystem<
  TData = Awaited<ReturnType<typeof updateSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest: BodyType<TLIntegrationsSystemUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSystem>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateSystem>>,
          TError,
          Awaited<ReturnType<typeof updateSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateSystem<
  TData = Awaited<ReturnType<typeof updateSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest: BodyType<TLIntegrationsSystemUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSystem>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateSystem>>,
          TError,
          Awaited<ReturnType<typeof updateSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateSystem<
  TData = Awaited<ReturnType<typeof updateSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest: BodyType<TLIntegrationsSystemUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update System
 */

export function useUpdateSystem<
  TData = Awaited<ReturnType<typeof updateSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest: BodyType<TLIntegrationsSystemUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateSystemQueryOptions({ sysRef }, tLIntegrationsSystemUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update System
 */
export const prefetchUpdateSystemQuery = async <
  TData = Awaited<ReturnType<typeof updateSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest: BodyType<TLIntegrationsSystemUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateSystemQueryOptions({ sysRef }, tLIntegrationsSystemUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update System
 */
export const invalidateUpdateSystem = async (
  queryClient: QueryClient,
  { sysRef }: UpdateSystemPathParameters,
  tLIntegrationsSystemUpdateRequest: BodyType<TLIntegrationsSystemUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateSystemQueryKey({ sysRef }, tLIntegrationsSystemUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary List System Audit
 */
export const listSystemAudit = (
  { sysRef }: ListSystemAuditPathParameters,
  params?: ListSystemAuditParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsAuditList>(
    { url: `/systems/${sysRef}/audit`, method: 'GET', params, signal },
    options,
  );
};

export const getListSystemAuditQueryKey = (
  { sysRef }: ListSystemAuditPathParameters,
  params?: ListSystemAuditParams,
) => {
  return ['systems', sysRef, 'audit', ...(params ? [params] : [])] as const;
};

export const getListSystemAuditQueryOptions = <
  TData = Awaited<ReturnType<typeof listSystemAudit>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: ListSystemAuditPathParameters,
  params?: ListSystemAuditParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemAudit>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSystemAuditQueryKey({ sysRef }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSystemAudit>>> = ({ signal }) =>
    listSystemAudit({ sysRef }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSystemAudit>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSystemAuditQueryResult = NonNullable<Awaited<ReturnType<typeof listSystemAudit>>>;
export type ListSystemAuditQueryError = ErrorType<TLHTTPValidationError>;

export function useListSystemAudit<
  TData = Awaited<ReturnType<typeof listSystemAudit>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSystemAuditPathParameters,
  params: undefined | ListSystemAuditParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemAudit>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSystemAudit>>,
          TError,
          Awaited<ReturnType<typeof listSystemAudit>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSystemAudit<
  TData = Awaited<ReturnType<typeof listSystemAudit>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSystemAuditPathParameters,
  params?: ListSystemAuditParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemAudit>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSystemAudit>>,
          TError,
          Awaited<ReturnType<typeof listSystemAudit>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSystemAudit<
  TData = Awaited<ReturnType<typeof listSystemAudit>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSystemAuditPathParameters,
  params?: ListSystemAuditParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemAudit>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List System Audit
 */

export function useListSystemAudit<
  TData = Awaited<ReturnType<typeof listSystemAudit>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: ListSystemAuditPathParameters,
  params?: ListSystemAuditParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemAudit>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSystemAuditQueryOptions({ sysRef }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List System Audit
 */
export const prefetchListSystemAuditQuery = async <
  TData = Awaited<ReturnType<typeof listSystemAudit>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: ListSystemAuditPathParameters,
  params?: ListSystemAuditParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemAudit>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListSystemAuditQueryOptions({ sysRef }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List System Audit
 */
export const invalidateListSystemAudit = async (
  queryClient: QueryClient,
  { sysRef }: ListSystemAuditPathParameters,
  params?: ListSystemAuditParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListSystemAuditQueryKey({ sysRef }, params) }, options);

  return queryClient;
};

/**
 * @summary List System Dependents
 */
export const listSystemDependents = (
  { sysRef }: ListSystemDependentsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsDependentList>(
    { url: `/systems/${sysRef}/dependents`, method: 'GET', signal },
    options,
  );
};

export const getListSystemDependentsQueryKey = ({ sysRef }: ListSystemDependentsPathParameters) => {
  return ['systems', sysRef, 'dependents'] as const;
};

export const getListSystemDependentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSystemDependents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: ListSystemDependentsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemDependents>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSystemDependentsQueryKey({ sysRef });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSystemDependents>>> = ({ signal }) =>
    listSystemDependents({ sysRef }, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSystemDependents>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSystemDependentsQueryResult = NonNullable<Awaited<ReturnType<typeof listSystemDependents>>>;
export type ListSystemDependentsQueryError = ErrorType<TLHTTPValidationError>;

export function useListSystemDependents<
  TData = Awaited<ReturnType<typeof listSystemDependents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSystemDependentsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemDependents>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSystemDependents>>,
          TError,
          Awaited<ReturnType<typeof listSystemDependents>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSystemDependents<
  TData = Awaited<ReturnType<typeof listSystemDependents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSystemDependentsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemDependents>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSystemDependents>>,
          TError,
          Awaited<ReturnType<typeof listSystemDependents>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSystemDependents<
  TData = Awaited<ReturnType<typeof listSystemDependents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListSystemDependentsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemDependents>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List System Dependents
 */

export function useListSystemDependents<
  TData = Awaited<ReturnType<typeof listSystemDependents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: ListSystemDependentsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemDependents>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSystemDependentsQueryOptions({ sysRef }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List System Dependents
 */
export const prefetchListSystemDependentsQuery = async <
  TData = Awaited<ReturnType<typeof listSystemDependents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: ListSystemDependentsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSystemDependents>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListSystemDependentsQueryOptions({ sysRef }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List System Dependents
 */
export const invalidateListSystemDependents = async (
  queryClient: QueryClient,
  { sysRef }: ListSystemDependentsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListSystemDependentsQueryKey({ sysRef }) }, options);

  return queryClient;
};

/**
 * @summary Purge System
 */
export const purgeSystem = (
  { sysRef }: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest: BodyType<TLIntegrationsSystemPurgeRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    {
      url: `/systems/${sysRef}/purge`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLIntegrationsSystemPurgeRequest,
      signal,
    },
    options,
  );
};

export const getPurgeSystemQueryKey = (
  { sysRef }: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest?: BodyType<TLIntegrationsSystemPurgeRequest>,
) => {
  return ['POST', 'systems', sysRef, 'purge', tLIntegrationsSystemPurgeRequest] as const;
};

export const getPurgeSystemQueryOptions = <
  TData = Awaited<ReturnType<typeof purgeSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest: BodyType<TLIntegrationsSystemPurgeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPurgeSystemQueryKey({ sysRef }, tLIntegrationsSystemPurgeRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof purgeSystem>>> = ({ signal }) =>
    purgeSystem({ sysRef }, tLIntegrationsSystemPurgeRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof purgeSystem>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PurgeSystemQueryResult = NonNullable<Awaited<ReturnType<typeof purgeSystem>>>;
export type PurgeSystemQueryError = ErrorType<TLHTTPValidationError>;

export function usePurgeSystem<
  TData = Awaited<ReturnType<typeof purgeSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest: BodyType<TLIntegrationsSystemPurgeRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeSystem>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof purgeSystem>>,
          TError,
          Awaited<ReturnType<typeof purgeSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePurgeSystem<
  TData = Awaited<ReturnType<typeof purgeSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest: BodyType<TLIntegrationsSystemPurgeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeSystem>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof purgeSystem>>,
          TError,
          Awaited<ReturnType<typeof purgeSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePurgeSystem<
  TData = Awaited<ReturnType<typeof purgeSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest: BodyType<TLIntegrationsSystemPurgeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Purge System
 */

export function usePurgeSystem<
  TData = Awaited<ReturnType<typeof purgeSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest: BodyType<TLIntegrationsSystemPurgeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPurgeSystemQueryOptions({ sysRef }, tLIntegrationsSystemPurgeRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Purge System
 */
export const prefetchPurgeSystemQuery = async <
  TData = Awaited<ReturnType<typeof purgeSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest: BodyType<TLIntegrationsSystemPurgeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof purgeSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPurgeSystemQueryOptions({ sysRef }, tLIntegrationsSystemPurgeRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Purge System
 */
export const invalidatePurgeSystem = async (
  queryClient: QueryClient,
  { sysRef }: PurgeSystemPathParameters,
  tLIntegrationsSystemPurgeRequest: BodyType<TLIntegrationsSystemPurgeRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPurgeSystemQueryKey({ sysRef }, tLIntegrationsSystemPurgeRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Test System
 */
export const testSystem = (
  { sysRef }: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull?: BodyType<TLIntegrationsSystemTestRequest | null> | null,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsTestResult>(
    {
      url: `/systems/${sysRef}/test`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLIntegrationsSystemTestRequestNull,
      signal,
    },
    options,
  );
};

export const getTestSystemQueryKey = (
  { sysRef }: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull?: BodyType<TLIntegrationsSystemTestRequest | null>,
) => {
  return ['POST', 'systems', sysRef, 'test', tLIntegrationsSystemTestRequestNull] as const;
};

export const getTestSystemQueryOptions = <
  TData = Awaited<ReturnType<typeof testSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull?: BodyType<TLIntegrationsSystemTestRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getTestSystemQueryKey({ sysRef }, tLIntegrationsSystemTestRequestNull);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof testSystem>>> = ({ signal }) =>
    testSystem({ sysRef }, tLIntegrationsSystemTestRequestNull, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof testSystem>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type TestSystemQueryResult = NonNullable<Awaited<ReturnType<typeof testSystem>>>;
export type TestSystemQueryError = ErrorType<TLHTTPValidationError>;

export function useTestSystem<
  TData = Awaited<ReturnType<typeof testSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull: undefined | BodyType<TLIntegrationsSystemTestRequest | null>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystem>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof testSystem>>,
          TError,
          Awaited<ReturnType<typeof testSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTestSystem<
  TData = Awaited<ReturnType<typeof testSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull?: BodyType<TLIntegrationsSystemTestRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystem>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof testSystem>>,
          TError,
          Awaited<ReturnType<typeof testSystem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTestSystem<
  TData = Awaited<ReturnType<typeof testSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull?: BodyType<TLIntegrationsSystemTestRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Test System
 */

export function useTestSystem<
  TData = Awaited<ReturnType<typeof testSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull?: BodyType<TLIntegrationsSystemTestRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getTestSystemQueryOptions({ sysRef }, tLIntegrationsSystemTestRequestNull, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Test System
 */
export const prefetchTestSystemQuery = async <
  TData = Awaited<ReturnType<typeof testSystem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull?: BodyType<TLIntegrationsSystemTestRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testSystem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getTestSystemQueryOptions({ sysRef }, tLIntegrationsSystemTestRequestNull, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Test System
 */
export const invalidateTestSystem = async (
  queryClient: QueryClient,
  { sysRef }: TestSystemPathParameters,
  tLIntegrationsSystemTestRequestNull?: BodyType<TLIntegrationsSystemTestRequest | null>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getTestSystemQueryKey({ sysRef }, tLIntegrationsSystemTestRequestNull) },
    options,
  );

  return queryClient;
};
